import { createContext, useContext, useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { UserProfile } from "@shared/schema";
import { useAuth } from "@/contexts/AuthContext";
import { userProfileService } from "@/services/userProfileService";

type UserProfileWithAvatar = UserProfile & { avatarUrl?: string };

interface UserProfileContextValue {
  profile: UserProfile | null;
  profileWithAvatar: UserProfileWithAvatar | null;
  isLoading: boolean;
  refetch: () => Promise<unknown>;
  refresh: () => void;
}

const UserProfileContext = createContext<UserProfileContextValue | undefined>(undefined);

export function UserProfileProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    userProfileService.clearCache();
  }, [user?.id]);

  const queryKey = useMemo(() => ["user-profile", user?.id ?? "anonymous"], [user?.id]);

  const {
    data: profile,
    isLoading,
    refetch,
  } = useQuery<UserProfile | null>({
    queryKey,
    queryFn: () => userProfileService.getUserProfile(),
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const profileWithAvatar = useMemo<UserProfileWithAvatar | null>(() => {
    if (!profile) return null;
    return {
      ...profile,
      avatarUrl: (profile as any).avatar_url,
    };
  }, [profile]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey });
  };

  return (
    <UserProfileContext.Provider
      value={{
        profile: profile ?? null,
        profileWithAvatar,
        isLoading,
        refetch,
        refresh,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
}

export function useUserProfile() {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error("useUserProfile must be used within a UserProfileProvider");
  }
  return context;
}
